import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { AppGlobals } from '@app/app.global';

export interface DeliveritOrderItem {
  productId: string;
  productName: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
}

export interface DeliveritOrderPricing {
  subtotal: number;
  tax: number;
  deliveryCharge: number;
  discount: number;
  totalAmount: number;
}

export interface DeliveritDeliveryAddress {
  addressLine1: string;
  city: string;
  state: string;
  pincode: string;
}

export interface DeliveritInvoice {
  invoiceNumber: string;
  invoiceUrl: string;
  storedS3Path: string;
  storedS3Key: string;
}

export interface DeliveritOrder {
  _id: string;
  orderId: string;
  orderStatus: 'NEW' | 'PICK-PACK' | 'INVOICING' | 'ASSIGNED' | 'OUT FOR DELIVERY' | 'DELIVERED' | 'RETURNED' | 'CLOSED';
  orderDate: string;
  storeUuid: string;
  entityId: string;
  customer: {
    name: string;
    mobile: string;
    email: string;
  };
  items: DeliveritOrderItem[];
  pricing: DeliveritOrderPricing;
  deliveryAddress: DeliveritDeliveryAddress;
  invoice: DeliveritInvoice;
  rabbitmqPublished: boolean;
  rabbitmqPublishedAt: string;
  webhookReceivedAt: string;
  lastUpdatedAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface DeliveritOrdersResponse {
  success: boolean;
  data: {
    orders: DeliveritOrder[];
    pagination?: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    };
  };
}

export interface DeliveritOrderResponse {
  success: boolean;
  data: DeliveritOrder;
}

export interface DeliveritInvoiceUrlResponse {
  success: boolean;
  data: {
    signedUrl: string | null;
    expiresIn: number;
  };
  message?: string;
}

@Injectable({
  providedIn: 'root'
})
export class DeliveritOrderService {

  constructor(private http: HttpClient, private appGlobals: AppGlobals) { }

  getOrders(params: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    sort_by?: string;
    sort_order?: string;
    store_uuid?: string;
    entityId?: string;
  }): Observable<DeliveritOrdersResponse> {
    let httpParams = new HttpParams();

    const entityId = params.entityId;

    Object.keys(params).forEach(key => {
      if (key === 'entityId') return;

      const value = params[key as keyof typeof params];
      if (value !== undefined && value !== null && value !== '') {
        httpParams = httpParams.set(key, value.toString());
      }
    });

    const headers = entityId ? { 'x-entity-ids': entityId } : {};

    return this.http.get<DeliveritOrdersResponse>(
      `${this.appGlobals.apiUrl.dashboard}deliverit/orders`,
      { params: httpParams, headers }
    );
  }

  getOrderById(orderId: string): Observable<DeliveritOrderResponse> {
    return this.http.get<DeliveritOrderResponse>(`${this.appGlobals.apiUrl.dashboard}deliverit/orders/${orderId}`);
  }

  getInvoiceSignedUrl(orderId: string): Observable<DeliveritInvoiceUrlResponse> {
    return this.http.get<DeliveritInvoiceUrlResponse>(`${this.appGlobals.apiUrl.dashboard}deliverit/orders/${orderId}/invoice`);
  }

  downloadInvoice(orderId: string): void {
    this.getInvoiceSignedUrl(orderId).subscribe({
      next: (response) => {
        if (response.success && response.data.signedUrl) {
          window.open(response.data.signedUrl, '_blank');
        }
      },
      error: (error) => {
        console.error('Error downloading DeliverIt invoice:', error);
      }
    });
  }
}
